import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import ConfirmationModal from "../../components/ConfirmationModal";
import Paginate from "../../components/Paginate";
import Alert from "../../components/Alert";
import Loader from "../../components/Loader";
import DefaultLayout from "../../layouts/DefaultLayout";

import "leaflet/dist/leaflet.css";
import { getListOrganizers } from "../../redux/actions/organizerActions";
import {
  getDashboardInfo,
  getDashboardOrdersStats,
} from "../../redux/actions/dashboardActions";

import DatePicker from "react-datepicker";
import { format, addDays } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { getDashboardOrderStatsReducer } from "../../redux/reducers/dashboardReducers";

// Add custom styles for DatePicker
import "./datepicker-custom.css";

function DashboardScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || "1";
  const dispatch = useDispatch();

  // Removed organizer select as it's no longer needed

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const listOrganizers = useSelector((state) => state.getListOrganizerReducer);
  const { organizers, loadingListOrganizers, errorListOrganizers } =
    listOrganizers;

  const dashboardInfo = useSelector((state) => state.getDashboardInfo);
  const {
    loadingDashboardInfo,
    dashInfo,
    successDashboardInfo,
    errorDashboardInfo,
  } = dashboardInfo;

  const dashboardOrderStats = useSelector(
    (state) => state.getDashboardOrderStats
  );
  const {
    loadingDashboardOrderStats,
    orders,
    pages,
    successDashboardOrderStats,
    errorDashboardOrderStats,
  } = dashboardOrderStats;

  const redirect = "/";

  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getDashboardInfo(startDate, endDate));
      dispatch(
        getDashboardOrdersStats(page, startDate, endDate)
      );
    }
  }, [navigate, userInfo, dispatch, page]);

  const formatDate = (dateString) => {
    if (dateString && dateString !== "") {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } else {
      return dateString;
    }
  };

  return (
    <DefaultLayout>
      <div className="animate-fadeIn">
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  ></path>
                </svg>
                Dashboard
              </a>
            </li>
          </ol>
        </nav>

        {/* Header with background */}
        <div
          className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg"
          style={{
            backgroundImage: "url('/assets/background-auth-screen.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Dashboard
                </h1>
                <p className="text-white/80 max-w-xl">
                  View your restaurant performance metrics and recent orders.
                </p>
              </div>
              <div className="mt-4 md:mt-0 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                <div className="flex items-center">
                  <div className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white/70 text-xs">Today</p>
                    <p className="text-white font-bold text-xl">{new Date().toLocaleDateString('en-US', {weekday: 'long', month: 'short', day: 'numeric'})}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/*  */}
        {/* Date Filter Section */}
        <div className="rounded-xl border border-stroke bg-white shadow-default  mb-8">
          <div className="p-6 border-b border-stroke">
            <h2 className="text-lg font-semibold text-black">Filter Data</h2>
            <p className="text-sm text-bodydark">Select a date range to filter dashboard data</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-black mb-2">Start Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-bodydark">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
                    </svg>
                  </div>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => {
                      setStartDate(date);
                      dispatch(
                        getDashboardInfo(
                          date !== "" && date !== null && date !== undefined
                            ? format(new Date(date), "yyyy-MM-dd")
                            : "",
                          endDate !== "" &&
                            endDate !== null &&
                            endDate !== undefined
                            ? format(new Date(endDate), "yyyy-MM-dd")
                            : ""
                        )
                      );
                      dispatch(
                        getDashboardOrdersStats(
                          "1",
                          date !== "" && date !== null && date !== undefined
                            ? format(new Date(date), "yyyy-MM-dd")
                            : "",
                          endDate !== "" &&
                            endDate !== null &&
                            endDate !== undefined
                            ? format(new Date(endDate), "yyyy-MM-dd")
                            : ""
                        )
                      );
                    }}
                    selectsStart
                    dateFormat="yyyy-MM-dd"
                    startDate={startDate}
                    endDate={endDate}
                    placeholderText="Select start date"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border-2 border-stroke focus:border-primary outline-none transition-colors duration-200"
                    // showMonthDropdown
                    // showYearDropdown
                    dropdownMode="select"
                    popperClassName="react-datepicker-left"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-2">End Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-bodydark">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
                    </svg>
                  </div>
                  <DatePicker
                    disabled={
                      startDate === "" ||
                      startDate === null ||
                      startDate === undefined
                    }
                    selected={endDate}
                    onChange={(date) => {
                      setEndDate(date);
                      dispatch(
                        getDashboardInfo(
                          startDate !== "" &&
                            startDate !== null &&
                            startDate !== undefined
                            ? format(new Date(startDate), "yyyy-MM-dd")
                            : "",
                          date !== "" && date !== null && date !== undefined
                            ? format(new Date(date), "yyyy-MM-dd")
                            : ""
                        )
                      );
                      dispatch(
                        getDashboardOrdersStats(
                          "1",
                          startDate !== undefined
                            ? format(new Date(startDate), "yyyy-MM-dd")
                            : "",
                          date !== "" && date !== null && date !== undefined
                            ? format(new Date(date), "yyyy-MM-dd")
                            : ""
                        )
                      );
                    }}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="yyyy-MM-dd"
                    minDate={startDate ? addDays(new Date(startDate), 1) : null}
                    placeholderText="Select end date"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border-2 border-stroke focus:border-primary outline-none transition-colors duration-200"
                    // showMonthDropdown
                    // showYearDropdown
                    dropdownMode="select"
                    popperClassName="react-datepicker-right"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  className="w-full py-3 px-4 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-all duration-200 flex items-center justify-center"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    dispatch(getDashboardInfo("", ""));
                    dispatch(getDashboardOrdersStats("1", "", ""));
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        </div>
        {/*  */}
        {/* Statistics Section */}
        <div className="rounded-xl border border-stroke bg-white shadow-default overflow-hidden mb-8">
          <div className="p-6 border-b border-stroke">
            <h2 className="text-lg font-semibold text-black">Performance Metrics</h2>
            <p className="text-sm text-bodydark">Key statistics for your restaurant</p>
          </div>
          <div className="p-6">
            {loadingDashboardInfo ? (
              <Loader />
            ) : errorDashboardInfo ? (
              <Alert type={"error"} message={errorDashboardInfo} />
            ) : (
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
                {/* Additional statistics */}
                <div className="rounded-xl bg-white border border-stroke p-6 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="bg-green-100 rounded-xl p-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-7 h-7 text-green-600"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
                        />
                      </svg>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-600">
                      Subtotal
                    </span>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-black mb-1">
                      ${parseFloat(dashInfo?.subtotal || 0).toFixed(2)}
                    </h3>
                    <p className="text-sm text-bodydark">Total subtotal before tax</p>
                  </div>
                </div>

                {/* Tax Card */}
                <div className="rounded-xl bg-white border border-stroke p-6 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="bg-blue-100 rounded-xl p-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-7 h-7 text-blue-600"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 6h.008v.008H6V6z"
                        />
                      </svg>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                      TAX (8%)
                    </span>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-black mb-1">
                      ${parseFloat(dashInfo?.tva_amount || 0).toFixed(2)}
                    </h3>
                    <p className="text-sm text-bodydark">Total tax collected</p>
                  </div>
                </div>

                {/* Delivery Fees Card */}
                <div className="rounded-xl bg-white border border-stroke p-6 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="bg-purple-100 rounded-xl p-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-7 h-7 text-purple-600"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
                        />
                      </svg>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-600">
                      Delivery
                    </span>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-black mb-1">
                      ${parseFloat(dashInfo?.delivery_fees || 0).toFixed(2)}
                    </h3>
                    <p className="text-sm text-bodydark">Total delivery fees</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
                {/* Orders Card */}
                <div className="rounded-xl bg-white border border-stroke p-6 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="bg-primary/10 rounded-xl p-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-7 h-7 text-primary"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                        />
                      </svg>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      Orders
                    </span>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-black mb-1">
                      {dashInfo?.total_orders ?? "0"}
                    </h3>
                    <p className="text-sm text-bodydark">Total orders processed</p>
                  </div>
                </div>

                {/* Revenue Card */}
                <div className="rounded-xl bg-white border border-stroke p-6 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="bg-secondary/10 rounded-xl p-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-7 h-7 text-secondary"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
                        />
                      </svg>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary">
                      Revenue
                    </span>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-black mb-1">
                      ${parseFloat(dashInfo?.revenu || 0).toFixed(2)}
                    </h3>
                    <p className="text-sm text-bodydark">Total revenue generated</p>
                  </div>
                </div>

                {/* Customers Card */}
                <div className="rounded-xl bg-white border border-stroke p-6 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="bg-accent/10 rounded-xl p-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-7 h-7 text-accent"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                        />
                      </svg>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent">
                      Customers
                    </span>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-black mb-1">
                      {dashInfo?.total_customers ?? "0"}
                    </h3>
                    <p className="text-sm text-bodydark">Total registered customers</p>
                  </div>
                </div>

                {/* Net Profit Card */}
                <div className="rounded-xl bg-white border border-stroke p-6 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="bg-primary/10 rounded-xl p-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-7 h-7 text-primary"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      Net Profit
                    </span>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-black mb-1">
                      ${parseFloat(dashInfo?.net_profit || 0).toFixed(2)}
                    </h3>
                    <p className="text-sm text-bodydark">Total net profit earned</p>
                  </div>
                </div>
              </div>
              </div>
            )}
          </div>
        </div>
        {/* Recent Orders Section */}
        <div className="rounded-xl border border-stroke bg-white shadow-default overflow-hidden mb-8">
          <div className="p-6 border-b border-stroke flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-black">Recent Orders</h2>
              <p className="text-sm text-bodydark">Latest orders from your customers</p>
            </div>
            <Link
              to="/orders"
              className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors duration-200 font-medium text-sm"
            >
              View All
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="bg-gray-2 text-left">
                    <th className="py-4 px-4 font-medium text-black text-sm border-b border-[#eee]">
                      Order ID
                    </th>
                    <th className="py-4 px-4 font-medium text-black text-sm border-b border-[#eee]">
                      Customer
                    </th>
                    <th className="py-4 px-4 font-medium text-black text-sm border-b border-[#eee]">
                      Amount
                    </th>
                    <th className="py-4 px-4 font-medium text-black text-sm border-b border-[#eee]">
                      Date
                    </th>
                    <th className="py-4 px-4 font-medium text-black text-sm border-b border-[#eee]">
                      Status
                    </th>
                    <th className="py-4 px-4 font-medium text-black text-sm border-b border-[#eee]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingDashboardOrderStats ? (
                    <tr>
                      <td colSpan="6" className="py-5 text-center">
                        <Loader />
                      </td>
                    </tr>
                  ) : errorDashboardOrderStats ? (
                    <tr>
                      <td colSpan="6" className="py-5 text-center">
                        <Alert type={"error"} message={errorDashboardOrderStats} />
                      </td>
                    </tr>
                  ) : orders && orders.length > 0 ? (
                    orders?.map((item, index) => (
                      <tr key={index} className="border-b border-[#eee] hover:bg-gray-1 transition-colors duration-200">
                        <td className="py-5 px-4">
                          <span className="font-medium text-sm text-black">#{item.id}</span>
                        </td>

                        <td className="py-5 px-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3 font-semibold">
                              {(item.client?.first_name?.[0] || "U") + (item.client?.last_name?.[0] || "")}
                            </div>
                            <div>
                              <h5 className="font-medium text-sm text-black">
                                {item.client?.first_name ?? "---"}{" "}
                                {item.client?.last_name ?? ""}
                              </h5>
                              <p className="text-xs text-bodydark">{item.client?.email ?? "---"}</p>
                            </div>
                          </div>
                        </td>

                        <td className="py-5 px-4">
                          <span className="text-sm font-medium text-black">
                            ${parseFloat(item.total_order).toFixed(2)}
                          </span>
                        </td>
                        <td className="py-5 px-4">
                          <span className="text-sm text-bodydark">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : "---"}
                          </span>
                        </td>
                        <td className="py-5 px-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
                            ${item.order_status === "DELIVERED" ? 'bg-green-100 text-green-600' :
                              item.order_status === "CANCELED" || item.order_status === "FAILED" ? 'bg-red-100 text-red-600' :
                              'bg-primary/10 text-primary'}`}>
                            {item.order_status || "---"}
                          </span>
                        </td>
                        <td className="py-5 px-4">
                          <div className="flex items-center space-x-3.5">
                            <Link
                              className="hover:text-primary text-body"
                              to={"/orders/detail/" + item.id}
                              title="View Details"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                                />
                              </svg>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-bodydark">
                        No orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/*  */}

        <div className="grid md:grid-cols-2 w-full container mt-5"></div>
      </div>
    </DefaultLayout>
  );
}

export default DashboardScreen;
